import React, { PropsWithChildren } from 'react'
import styled, { css, DefaultTheme, StyledComponent } from 'styled-components'
import { getDisplayName } from '@sketch/utils'
import {
  alignItems,
  AlignItemsProps,
  flex,
  flexDirection,
  FlexDirectionProps,
  FlexProps as SyledFlexProps,
  flexWrap,
  FlexWrapProps,
  justifyContent,
  JustifyContentProps,
  alignSelf,
  justifySelf,
  JustifySelfProps,
  AlignSelfProps,
} from 'styled-system'

export interface FlexProps
  extends SyledFlexProps,
    FlexWrapProps,
    FlexDirectionProps,
    JustifyContentProps,
    JustifySelfProps,
    AlignSelfProps,
    AlignItemsProps {
  display?: never
  is?: never
}

const FlexCss = css`
  display: flex;
  ${flex}
  ${flexWrap}
  ${flexDirection}
  ${justifyContent}
  ${alignItems}
  ${alignSelf}
  ${justifySelf}
`

interface CompPropsWithoutIs
  extends Omit<React.ComponentPropsWithRef<any>, 'is'>,
    PropsWithChildren {
  is: never
}

type WithFlexCompArg =
  | keyof React.JSX.IntrinsicElements
  | React.ComponentType<CompPropsWithoutIs>

export function withFlex<C extends WithFlexCompArg>(Comp: C) {
  /**
   * We are passing `any` to styled here to avoid increased TypeScript complexity.
   * The original typing should be:
   * ```
   *   styled(Comp)<FlexProps>` ... `
   * ```
   *
   * However, this causes TypeScript to bloat and slow down significantly to the point that the whole
   * process crashes on some machines/projects (Particularly CI).
   *
   * As a workaround, we are using `any` here and casting the final result to
   * `StyledComponent<C, DefaultTheme, FlexProps, never>`, which is the expected type.
   *
   * This way, we avoid TypeScript bloat in this file.
   */
  // eslint-disable-next-line no-unexpected-multiline
  const styledComp = styled(Comp)<any>`
    ${FlexCss}
  ` as StyledComponent<C, DefaultTheme, FlexProps, never>

  styledComp.displayName = `withFlex(${getDisplayName(Comp)})`
  return styledComp
}
